import axios from 'axios'
import { isEqual, uniqWith } from 'lodash'

import { Country } from 'shared/types/types'

import { CityDTO, StreetDTO, YapiGeocoderResponse, YApiGeoObject, YApiKinds } from './types'

export class YApi {
  /* apiKey = '7238887c-e512-458a-a272-3e6b1c8fa157' */
  /* apiKey = 'e0976540-23ba-45d8-8beb-3691dc76e76c' */
  apiKey = '0b1bf77e-56d1-4ff1-8df4-47b4af1f64b7'

  apiKit = axios.create({
    baseURL: 'https://geocode-maps.yandex.ru/',
    timeout: 10000,
  })

  getCitiesByCityName = async (cityName: string, countryCode: Country, lang: 'ru' | 'en'): Promise<{ data: CityDTO[] }> => {
    const geocoderResponseLocalities = await yApi.callYapi({
      geocode: cityName,
      results: 100,
      lang: lang,
    })

    const toponyms = uniqWith(geocoderResponseLocalities.response.GeoObjectCollection.featureMember, isEqual)

    // При поиске имени нельзя указать kind поэтому в ответе могут быть и реки и озёра и города и т.п.,
    // Поэтому нужно отфильтровать.
    const localities = yApi.filterYApiGeoObjectsByKinds(toponyms, 'locality')

    const localitiesFilteredByCountry = yApi.filterYApiGeoObjectsByCountry(localities, countryCode)

    // Если в ответе есть населённые пункты (kind === 'locality'), то они возвращаются,
    // если населённых пунктов нет, то пытается найти населённые пункты около первого топонима который был в ответе.
    // (так например если искать "Москва", то не возвращается населённый пункт, а только один топоним "Московская Область")
    if (localitiesFilteredByCountry.length) {
      return {
        data: localitiesFilteredByCountry.map((item, index) => yApi.convertYApiGeoObjectToCityOption(item, index + 1)),
      }
    } else if (toponyms[0]) {
      return this.getCitiesByCoords(toponyms[0].GeoObject.Point.pos, countryCode, lang)
    }

    return { data: [] }
  }

  async getCitiesByCoords(coordinates: string, countryCode: Country, lang: 'ru' | 'en'): Promise<{ data: CityDTO[] }> {
    const resp = await yApi.getLocalitiesByCoords(coordinates, lang)

    const filteredByCountry = this.filterYApiGeoObjectsByCountry(
      resp.response.GeoObjectCollection.featureMember,
      countryCode,
    )

    return {
      data: this.getCitiesFromFeatureMember(filteredByCountry),
    }
  }

  /**
   * Убирает дубликаты. Преобразует к массиву CityDTO.
   */
  getCitiesFromFeatureMember(featureMember: YApiGeoObject[]): CityDTO[] {
    const uniqLocalities = uniqWith(featureMember, yApi.isEqualYApiGeoObjects).map((item, index) =>
      yApi.convertYApiGeoObjectToCityOption(item, index + 1),
    )

    return uniqLocalities
  }

  /**
   * Сперва сравнивает по имени, типу и адресу (для оптимизации), а потом приступает к глубокому сравнению
   */
  isEqualYApiGeoObjects(yApiGeoObjectsA: YApiGeoObject, yApiGeoObjectsB: YApiGeoObject): boolean {
    return (
      yApiGeoObjectsA.GeoObject.name === yApiGeoObjectsB.GeoObject.name &&
      yApiGeoObjectsA.GeoObject.metaDataProperty.GeocoderMetaData.AddressDetails.Country.AddressLine ===
        yApiGeoObjectsB.GeoObject.metaDataProperty.GeocoderMetaData.AddressDetails.Country.AddressLine &&
      yApiGeoObjectsA.GeoObject.metaDataProperty.GeocoderMetaData.kind ===
        yApiGeoObjectsB.GeoObject.metaDataProperty.GeocoderMetaData.kind &&
      isEqual(yApiGeoObjectsA, yApiGeoObjectsB)
    )
  }

  convertYApiGeoObjectToCityOption(yApiGeoObjects: YApiGeoObject, id: number): CityDTO {
    return {
      id,
      prefix: yApiGeoObjects.GeoObject.name,
      name: yApiGeoObjects.GeoObject.description,
      formattedAddress: yApiGeoObjects?.GeoObject.metaDataProperty?.GeocoderMetaData?.Address?.formatted ?? '',
    }
  }

  /**
   * Возвращает новый массив с YApiGeoObject, у которых типы (kind) совпадают с переданными во втором параметре.
   */
  filterYApiGeoObjectsByKinds(yApiGeoObjects: YApiGeoObject[], kinds: YApiKinds[] | YApiKinds): YApiGeoObject[] {
    const filteredKinds = Array.isArray(kinds) ? kinds : [kinds]

    return yApiGeoObjects.filter((item) => {
      const kind = item.GeoObject.metaDataProperty.GeocoderMetaData.kind

      return filteredKinds.includes(kind)
    })
  }

  filterYApiGeoObjectsByCountry(yApiGeoObjects: YApiGeoObject[], countryCode: Country): YApiGeoObject[] {
    return yApiGeoObjects.filter((item) => {
      return item.GeoObject.metaDataProperty.GeocoderMetaData.AddressDetails.Country.CountryNameCode === countryCode
    })
  }

  getLocalitiesByCoords(coordinates: string, lang: 'ru' | 'en'): Promise<YapiGeocoderResponse> {
    const eleven = coordinates.split(' ').join(',')
    return new Promise<any>((resolve) => {
      this.callYapi({ geocode: eleven, kind: 'locality', lang: lang, }).then((res) => {
        resolve(res)
      })
    })
  }

  getStreet = (description: string, lang: 'ru' | 'en') => {
    return new Promise<StreetDTO[]>((resolve) => {
      const geocode = description

      this.callYapi({ geocode, kind: 'street', lang: lang, })
        .then((res) => {
          let response: StreetDTO[] = []
          const obj = res.response.GeoObjectCollection.featureMember[0]

          response = this.getAddressFromFeatured(res)
          this.getHousesByCoordinates(obj.GeoObject.Point.pos, lang, response).then((res) => resolve(res))
        })
        .catch((error) => new Error(error))
    })
  }

  getHousesByCoordinates(coordinates: string, lang: 'ru' | 'en', anyStreets?: StreetDTO[]): Promise<StreetDTO[]> {
    return new Promise((resolve) => {
      this.getHousesByCoords(coordinates, lang).then((res) => {
        const response = anyStreets
          ? anyStreets?.concat(this.getAddressFromFeatured(res))
          : this.getAddressFromFeatured(res)

        resolve(
          response.filter(
            (v, i, a) =>
              a.findIndex((t) => `${t.name}${t.house}${t.description}` === `${v.name}${v.house}${v.description}`) === i,
          ),
        )
      })
    })
  }

  getAddressFromFeatured(response: YapiGeocoderResponse): StreetDTO[] {
    if (!response.response) return []
    const objects = response.response.GeoObjectCollection.featureMember
    return objects.map((geo) => {
      const obj = geo.GeoObject
      const pos = obj.Point.pos.split(' ')
      const house = obj.metaDataProperty.GeocoderMetaData.Address.Components.find((cmp) => cmp.kind === 'house')
      const street = obj.metaDataProperty.GeocoderMetaData.Address.Components.find((cmp) => cmp.kind === 'street')
      const locality = obj.metaDataProperty.GeocoderMetaData.Address.Components.find((cmp) => cmp.kind === 'locality')

      return {
        name: obj.name,
        street: street ? street.name : locality ? locality.name : '',
        description: obj.description,
        house: house ? house.name : '',
        postCode: 1,
        kind: obj.metaDataProperty.GeocoderMetaData.kind,
        precision: obj.metaDataProperty.GeocoderMetaData.precision,
        lat: pos[1],
        lng: pos[0],
      }
    })
  }

  getHousesByCoords(coordinates: string, lang: 'ru' | 'en'): Promise<YapiGeocoderResponse> {
    const eleven = coordinates.split(' ').join(',')
    return new Promise<YapiGeocoderResponse>((resolve) => {
      this.callYapi({ geocode: eleven, kind: 'house', lang: lang, }).then((res) => {
        resolve(res)
      })
    })
  }

  get(url: string, fields?: any): Promise<any> {
    const searchParamsStr = fields ? new URLSearchParams(fields) : undefined

    return new Promise<any>((resolve, reject) => {
      const requestUrl = url + (searchParamsStr ? `?${searchParamsStr}` : '')
      this.apiKit
        .get(requestUrl, {})
        .then((data) => resolve(data.data))
        .catch((e) => {
          reject(e)
        })
    })
  }

  callYapi(params: any): Promise<YapiGeocoderResponse> {
    params.apikey = this.apiKey
    params.format = 'json'
    return this.get('1.x/', params)
  }
}

export const yApi = new YApi()
